import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useAttemptData,
  useAttemptSaveMutation,
  useAttemptProcessMutation,
  useAttemptSummary,
  useAttemptReview,
  useViewAttempt,
  useQuizUserBestGrade,
  useQuizUserAttempts,
  useQuizzesByCourses,
} from '../../../../hooks/useMoodleQuiz';
import { useQueryClient } from '@tanstack/react-query'; //-tech-debt-react-query;
import type { MoodleModule } from '../../../../types/moodleTypes';
import QuizTimer from './QuizTimer';
import QuizActionsBar from './QuizActionsBar';
import QuestionHTML from './QuestionHTML';
import StbLoader from '../../../security-toolbox-ui/StbLoader';
import type { MoodleInstance } from '../../../../hooks/useMoodleHooks';

function formToObject(form: HTMLFormElement): Record<string, any> {
  const fd = new FormData(form);
  const obj: Record<string, any> = {};
  fd.forEach((value, key) => {
    if (key in obj) {
      const prev = obj[key];
      obj[key] = Array.isArray(prev) ? [...prev, value] : [prev, value];
    } else {
      obj[key] = value;
    }
  });
  return obj;
}

const AUTOSAVE_DELAY_MS = 1200;

interface Props {
  courseId: string | number;
  quizId: string | number;
  attemptId: string | number;
  instance: MoodleInstance;
  modules?: MoodleModule[];
  onAttemptFinished?: (attemptId: number) => void;
  onBackToEntry?: () => void;
  onBackToCourse?: () => void;
}

const SafeLoader: React.FC<{ isLoading: boolean; message?: string; children?: React.ReactNode }> = ({
                                                                                                      isLoading,
                                                                                                      message,
                                                                                                      children,
                                                                                                    }) => {
  if (!isLoading) return <>{children}</>;
  try {
    return <StbLoader isLoading={true} />;
  } catch {
    return <div>{message || 'Loading...'}</div>;
  }
};

export default function MoodleQuizWrapper({
                                            courseId,
                                            quizId,
                                            attemptId,
                                            instance,
                                            modules = [],
                                            onAttemptFinished,
                                            onBackToEntry,
                                            onBackToCourse,
                                          }: Props) {
  const [showLoadingModal, setShowLoadingModal] = useState(false);

  const qid = quizId != null ? Number(quizId) : undefined;
  const aid = attemptId != null ? Number(attemptId) : undefined;
  const cid = courseId != null ? Number(courseId) : undefined;

  const [step, setStep] = useState<'attempt' | 'summary'>('attempt');
  const [questionIndex, setQuestionIndex] = useState<number>(0);

  const formRef = useRef<HTMLFormElement | null>(null);
  const autosaveTimerRef = useRef<number | null>(null);
  const queryClient = useQueryClient();

  const {
    data: quizInfo,
    isLoading: loadingQuizInfo,
    error: quizInfoError,
  } = useQuizzesByCourses(cid, { enabled: !!cid }, instance);

  const {
    data: attemptData,
    isLoading: attemptLoading,
    error: attemptError,
  } = useAttemptData(aid, 0, { enabled: !!aid }, instance);

  const moodleUserId = (attemptData as any)?.userid;

  useQuizUserBestGrade(qid, moodleUserId, { enabled: !!qid && !!moodleUserId }, instance);
  useQuizUserAttempts(qid, { userid: moodleUserId }, { enabled: !!qid && !!moodleUserId }, instance);

  const { refetch: viewAttempt } = useViewAttempt(aid, { enabled: false }, instance);

  const {
    data: summary,
    refetch: refetchSummary,
    isFetching: summaryLoading,
    error: summaryError,
  } = useAttemptSummary(
    aid,
    undefined,
    {
      enabled: !!aid,
      refetchOnWindowFocus: false,
    },
    instance
  );

  useAttemptReview(aid, undefined, { enabled: false }, instance);

  const { mutate: saveAttempt, isPending: saving } = useAttemptSaveMutation(aid ?? 0, {}, instance);
  const { mutate: processAttempt, isPending: processing } = useAttemptProcessMutation(aid ?? 0, {}, instance);

  const moduleInfo = useMemo(() => modules?.find((m) => m.instance === qid), [modules, qid]);
  const currentQuizInfo = useMemo(() => quizInfo?.find((q: any) => q.id === qid), [quizInfo, qid]);

  const allQuestions = useMemo(() => {
    return Array.isArray((attemptData as any)?.questions) ? (attemptData as any).questions : [];
  }, [attemptData]);

  const totalQuestions = allQuestions.length;

  const currentQuestions = useMemo(() => {
    if (!allQuestions.length) return [];
    const q = allQuestions[questionIndex];
    return q ? [q] : [];
  }, [allQuestions, questionIndex]);

  const totalPages = totalQuestions;
  const hasNextPage = questionIndex < totalPages - 1;
  const hasPrevPage = questionIndex > 0;

  const timeClose = useMemo(() => {
    return (attemptData as any)?.timeclose ?? (attemptData as any)?.endtime ?? (attemptData as any)?.duedate;
  }, [attemptData]);

  useEffect(() => {
    if (aid) {
      viewAttempt();
    }
  }, [aid, viewAttempt]);

  useEffect(() => {
    return () => {
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (questionIndex >= totalQuestions && totalQuestions > 0) {
      setQuestionIndex(totalQuestions - 1);
    }
  }, [questionIndex, totalQuestions]);

  const goToQuestion = useCallback(
    (index: number) => {
      if (formRef.current) {
        const payload = formToObject(formRef.current);
        saveAttempt({ fields: payload });
      }
      setQuestionIndex(index);
    },
    [saveAttempt]
  );

  const onFormChange = useCallback(() => {
    if (!formRef.current) return;
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);

    autosaveTimerRef.current = window.setTimeout(() => {
      if (!formRef.current) return;
      const payload = formToObject(formRef.current);
      saveAttempt({ fields: payload }, { onSuccess: refetchSummary });
    }, AUTOSAVE_DELAY_MS);
  }, [saveAttempt, refetchSummary]);

  const handlePrev = useCallback(() => {
    if (!hasPrevPage || !formRef.current) return;

    const payload = formToObject(formRef.current);
    saveAttempt(
      { fields: payload },
      {
        onSuccess: () => {
          setQuestionIndex((cur) => Math.max(0, cur - 1));
        },
        onError: () => {
          setQuestionIndex((cur) => Math.max(0, cur - 1));
        },
      }
    );
  }, [hasPrevPage, saveAttempt]);

  const handleNext = useCallback(() => {
    if (!hasNextPage || !formRef.current) return;

    const payload = formToObject(formRef.current);
    saveAttempt(
      { fields: payload },
      {
        onSuccess: () => {
          setQuestionIndex((cur) => Math.min(totalPages - 1, cur + 1));
        },
        onError: () => {
          setQuestionIndex((cur) => Math.min(totalPages - 1, cur + 1));
        },
      }
    );
  }, [hasNextPage, saveAttempt, totalPages]);

  const handleManualSave = useCallback(() => {
    if (!formRef.current) return;
    const payload = formToObject(formRef.current);
    saveAttempt({ fields: payload }, { onSuccess: refetchSummary });
  }, [saveAttempt, refetchSummary]);

  const handleGoToQuestion = useCallback(
    (index: number) => {
      if (!formRef.current) return;
      const payload = formToObject(formRef.current);
      saveAttempt(
        { fields: payload },
        {
          onSuccess: () => goToQuestion(index),
          onError: () => goToQuestion(index),
        }
      );
    },
    [saveAttempt, goToQuestion]
  );

  const finishAndNotifyParent = useCallback(() => {
    if (!aid) return;

    let tries = 0;

    const poll = () => {
      queryClient
        .refetchQueries({ queryKey: ['MoodleAttemptSummary', aid, '{}', instance] })
        .then(() => {
          const summaryData = queryClient.getQueryData([
            'MoodleAttemptSummary',
            aid,
            '{}',
            instance,
          ]) as { state?: string; errorcode?: string } | undefined;

          const state = String(summaryData?.state || '').toLowerCase();
          const errorCode = String(summaryData?.errorcode || '').toLowerCase();

          if (state === 'finished' || errorCode === 'attemptalreadyclosed') {
            setShowLoadingModal(false);
            setTimeout(() => {
              onAttemptFinished?.(aid);
            }, 0);
          } else if (tries < 15) {
            tries++;
            setTimeout(poll, 700);
          } else {
            setShowLoadingModal(false);
            setTimeout(() => {
              onAttemptFinished?.(aid);
            }, 0);
          }
        })
        .catch(() => {
          if (tries < 15) {
            tries++;
            setTimeout(poll, 700);
          } else {
            setShowLoadingModal(false);
            setTimeout(() => {
              onAttemptFinished?.(aid);
            }, 0);
          }
        });
    };

    poll();
  }, [aid, queryClient, instance, onAttemptFinished]);

  const handleSubmitAttempt = useCallback(
    (opts?: { timeup?: boolean; finish?: boolean }) => {
      if (!formRef.current || !aid) return;
      const payload = formToObject(formRef.current);
      setShowLoadingModal(true);

      saveAttempt(
        { fields: payload },
        {
          onSuccess: () => {
            processAttempt(
              {
                ...payload,
                timeup: opts?.timeup ? 1 : 0,
                finishattempt: opts?.finish ? 1 : 0,
              },
              {
                onSuccess: () => {
                  finishAndNotifyParent();
                },
                onError: (err: any) => {
                  setShowLoadingModal(false);
                  alert(`Submission failed: ${err.message}`);
                },
              }
            );
          },
          onError: (err: any) => {
            setShowLoadingModal(false);
            alert(`Save failed: ${err.message}`);
          },
        }
      );
    },
    [saveAttempt, processAttempt, aid, finishAndNotifyParent]
  );

  const handleTimeUp = useCallback(() => {
    handleSubmitAttempt({ timeup: true, finish: true });
  }, [handleSubmitAttempt]);

  const handleShowSummary = useCallback(() => {
    setStep('summary');
    refetchSummary();
  }, [refetchSummary]);

  const handleSubmitAllAndFinish = useCallback(() => {
    handleSubmitAttempt({ finish: true });
  }, [handleSubmitAttempt]);

  if (!aid || !qid || !cid) {
    return (
      <div style={{ padding: 16 }}>
        <p>Invalid quiz or attempt.</p>
        {onBackToEntry && <button onClick={onBackToEntry}>Go Back</button>}
      </div>
    );
  }

  if ((attemptLoading && !attemptData) || loadingQuizInfo) {
    return <SafeLoader isLoading={true} message="Loading quiz attempt..." />;
  }

  if (attemptError) {
    return <p role="alert" style={{ color: 'red' }}>{(attemptError as Error).message}</p>;
  }

  if (quizInfoError) {
    return <p role="alert" style={{ color: 'red' }}>{(quizInfoError as Error).message}</p>;
  }

  if (summaryError) {
    return <p role="alert" style={{ color: 'red' }}>{(summaryError as Error).message}</p>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100%', padding: 16, gap: 12 }}>
      <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <div>
          <h2 style={{ margin: 0 }}>{moduleInfo?.name ?? (currentQuizInfo as any)?.name ?? 'Quiz'}</h2>
          {(attemptData as any)?.attemptnumber != null && <small>Attempt {(attemptData as any).attemptnumber}</small>}
        </div>
        {timeClose ? <QuizTimer endTime={timeClose} onTimeUp={handleTimeUp} /> : null}
      </header>

      {(onBackToEntry || onBackToCourse) && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
          {onBackToEntry && (
            <button type="button" className="btn btn-outline" onClick={onBackToEntry}>
              ← Back to Quiz
            </button>
          )}
          {onBackToCourse && (
            <button type="button" className="btn btn-outline" onClick={onBackToCourse}>
              Back to Course
            </button>
          )}
        </div>
      )}

      {step === 'attempt' && (
        <>
          {totalPages > 1 && (
            <div
              style={{
                background: '#f8f9fa',
                border: '1px solid #e9ecef',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '1rem',
              }}
            >
              <h4 style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', color: '#495057' }}>
                Question Navigator
              </h4>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(40px, 1fr))',
                  gap: '0.5rem',
                  maxWidth: '400px',
                }}
              >
                {Array.from({ length: totalPages }, (_, i) => (
                  <button
                    key={i}
                    onClick={() => handleGoToQuestion(i)}
                    disabled={processing || saving}
                    style={{
                      width: '40px',
                      height: '40px',
                      border: `2px solid ${questionIndex === i ? '#007bff' : '#dee2e6'}`,
                      borderRadius: '6px',
                      background: questionIndex === i ? '#007bff' : '#fff',
                      color: questionIndex === i ? 'white' : '#495057',
                      cursor: 'pointer',
                      fontWeight: '500',
                    }}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
            </div>
          )}

          <QuizActionsBar
            currentPage={questionIndex}
            totalPages={totalPages}
            canGoPrev={hasPrevPage}
            canGoNext={hasNextPage}
            onPrev={handlePrev}
            onNext={handleNext}
            onSave={handleManualSave}
            onSummary={handleShowSummary}
            onFinish={handleSubmitAllAndFinish}
            saving={saving}
            submitting={processing}
          />

          <form ref={formRef} onChange={onFormChange} onSubmit={(e) => e.preventDefault()}>
            {currentQuestions.length > 0 ? (
              currentQuestions.map((q: any, idx: number) => {
                const key = q?.slot ?? q?.id ?? idx;
                return (
                  <div key={key} style={{ marginBottom: 24 }}>
                    <QuestionHTML html={(q?.html ?? q?.questiontext ?? '') as string} />
                  </div>
                );
              })
            ) : (
              <p>No questions on this page.</p>
            )}
          </form>
        </>
      )}

      {step === 'summary' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <h3 style={{ margin: 0 }}>Attempt Summary</h3>
          <SafeLoader isLoading={summaryLoading} message="Loading summary...">
            {summary ? (
              Array.isArray((summary as any)?.questions) ? (
                <div>
                  <p style={{ marginBottom: '1rem', color: '#666' }}>
                    Click on any question below to review or modify your answer:
                  </p>
                  <div style={{ display: 'grid', gap: '0.5rem' }}>
                    {(summary as any).questions.map((s: any, i: number) => (
                      <button
                        key={s?.slot ?? s?.id ?? i}
                        onClick={() => {
                          setStep('attempt');
                          goToQuestion(i);
                        }}
                        style={{
                          padding: '0.75rem 1rem',
                          border: '1px solid #dee2e6',
                          borderRadius: '6px',
                          background: '#fff',
                          cursor: 'pointer',
                          textAlign: 'left',
                        }}
                      >
                        <strong>Question {s?.number ?? i + 1}</strong>: {s?.state ?? s?.status ?? 'Not answered'}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <pre>{JSON.stringify(summary, null, 2)}</pre>
              )
            ) : (
              <p>No summary available.</p>
            )}
          </SafeLoader>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setStep('attempt')} disabled={processing}>
              Back to Attempt
            </button>
            <button onClick={handleSubmitAllAndFinish} disabled={processing}>
              Submit All and Finish
            </button>
          </div>
        </div>
      )}

      {showLoadingModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
          }}
        >
          <div
            style={{
              background: '#fff',
              padding: '2rem',
              borderRadius: '8px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <h3>Submitting your quiz...</h3>
            <p>Please wait while we finalize your attempt.</p>
            <SafeLoader isLoading={true} message="Processing submission..." />
          </div>
        </div>
      )}
    </div>
  );
}